import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react'

import { ThemeContext, type ThemeMode } from './ThemeContext'

const STORAGE_KEY = 'kv.mode'

function readInitialMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark') return saved
  } catch {
    /* хранилище недоступно — берём тему по умолчанию */
  }
  return 'dark'
}

/**
 * Атрибут на <html> ставим синхронно, до перерисовки React: графики читают
 * CSS-переменные во время рендера и должны увидеть уже новую тему.
 */
function applyMode(mode: ThemeMode) {
  document.documentElement.dataset.mode = mode
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const initial = readInitialMode()
    applyMode(initial)
    return initial
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      /* ignore */
    }
  }, [mode])

  const setMode = useCallback((next: ThemeMode) => {
    applyMode(next)
    setModeState(next)
  }, [])

  const toggle = useCallback(() => setMode(mode === 'dark' ? 'light' : 'dark'), [mode, setMode])

  const value = useMemo(() => ({ mode, setMode, toggle }), [mode, setMode, toggle])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
